import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { type Patient } from '@avanta/shared/types/patients';
import { dateFromBirthParts, getBirthDateParts } from '@avanta/shared/utils/date';
import { type Control, type FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUpdatePatient } from 'hooks/useUpdatePatient';
import { useUserStore } from 'stores/user';
import { profileForm } from './model';

type ProfileFormData = {
  nickname?: string;
  nif?: string;
  firstName?: string;
  surname?: string;
  contactPhone?: string;
  dateOfBirth: Date | undefined;
  gender?: string;
  timeZone?: string;
};

export const ProfileForm = (): React.ReactElement | null => {
  const user = useUserStore(state => state.user);
  const isEmployee = useUserStore(state => state.isEmployee);
  const isPatient = useUserStore(state => state.isPatient);
  const showProfileForm = isEmployee || isPatient;
  const { mutate: updatePatient } = useUpdatePatient();
  const { t } = useTranslation(LOCIZE_NS.PROFILE);
  const {
    register,
    control,
    handleSubmit,
    formState: { isDirty, errors, isValid },
    reset,
  } = useForm<ProfileFormData>({
    defaultValues: {
      nickname: user?.nickname,
      nif: user?.nif,
      firstName: user?.firstName,
      surname: user?.surname,
      contactPhone: user?.contactPhone,
      dateOfBirth: dateFromBirthParts(user?.monthOfBirth, user?.yearOfBirth, user?.dayOfBirth),
      gender: user?.gender?.toString(),
      timeZone: user?.timeZone,
    },
    mode: 'onChange',
  });

  const handleOnSubmit = async (data: ProfileFormData): Promise<void> => {
    if (!user?.id) return;

    const { dateOfBirth: birthDate, ...formValues } = data;

    const payload = {
      ...user,
      ...formValues,
      ...(birthDate && getBirthDateParts(birthDate)),
    } as Patient;

    updatePatient(payload);
    reset({
      ...formValues,
      dateOfBirth: birthDate,
    });
  };

  if (!showProfileForm) {
    return null;
  }

  return (
    <Card className="bg-white" title={t('ProfileSettings.heading')}>
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(handleOnSubmit)}>
        <div className="grid gap-6 am-lg:grid-cols-2">
          {Object.entries(profileForm).map(([id, input]) => {
            const { component: Component } = input;
            const isControllerComponent = Component !== Input;
            const formId = id as keyof ProfileFormData;
            const formErrors = errors[formId];
            const rules = {
              required: input.required,
              ...(input.pattern && { pattern: input.pattern }),
              ...(input.validate && { validate: input.validate }),
              ...(input.minLength && { minLength: input.minLength }),
              ...(input.maxLength && { maxLength: input.maxLength }),
            };
            const helperText = formErrors?.message ? t(formErrors.message) : undefined;
            const isRequired = Boolean(input.required);

            return (
              <div key={id} className={input.className}>
                {isControllerComponent ? (
                  <Component
                    control={control as unknown as Control<FieldValues>}
                    name={formId}
                    className="w-full"
                    label={t(input.label)}
                    placeholder={t(input.placeholder ?? '')}
                    options={input.options ?? []}
                    variant={formErrors ? 'invalid' : 'filled'}
                    helperText={helperText}
                    required={isRequired}
                    rules={rules}
                    calendarProps={input.calendarProps}
                  />
                ) : (
                  <Input
                    type={input.type}
                    className="w-full"
                    label={t(input.label)}
                    placeholder={t(input.placeholder ?? '')}
                    variant={formErrors ? 'invalid' : 'filled'}
                    helperText={helperText}
                    required={isRequired}
                    {...register(formId, rules)}
                  />
                )}
              </div>
            );
          })}
        </div>
        <Button variant="primary" type="submit" className="w-fit" disabled={!isDirty || !isValid}>
          {t('ProfileSettings.cta')}
        </Button>
      </form>
    </Card>
  );
};
